import React, { useEffect, useState } from 'react';
import { Alert, Button, Form } from 'antd';
import { Col, Container, Row } from 'react-bootstrap';
import IyeInput from 'components/IyeInput';
import logo from "../assets/images/yelogo.png";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { clearAuthError, loginAdmin } from 'store/slices/authSlice';

interface LoginProps {
  children?: any,
}

const ADMIN_REMEMBER_ME_KEY = "admin_remember_me";
const ADMIN_REMEMBER_PHONE_KEY = "admin_remember_phone";

const Login: React.FC<LoginProps> = (props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { error, isAuthenticated, loading, token } = useAppSelector((state) => state.auth);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [form] = Form.useForm();

  const onFinish = async (values: { phone: string; password: string }) => {
    const result = await dispatch(loginAdmin(values));

    if (loginAdmin.fulfilled.match(result)) {
      if (rememberMe) {
        localStorage.setItem(ADMIN_REMEMBER_ME_KEY, "true");
        localStorage.setItem(ADMIN_REMEMBER_PHONE_KEY, values.phone);
      } else {
        localStorage.removeItem(ADMIN_REMEMBER_ME_KEY);
        localStorage.removeItem(ADMIN_REMEMBER_PHONE_KEY);
      }

      navigate("/admin-panel", { replace: true });
    }
  };
  const onFinishFailed = () => { };

  useEffect(() => {
    if (isAuthenticated && token) {
      navigate("/admin-panel", { replace: true });
    }
  }, [isAuthenticated, navigate, token]);

  useEffect(() => {
    return () => {
      dispatch(clearAuthError());
    };
  }, [dispatch]);

  useEffect(() => {
    const shouldRemember = localStorage.getItem(ADMIN_REMEMBER_ME_KEY) === "true";
    const rememberedPhone = localStorage.getItem(ADMIN_REMEMBER_PHONE_KEY) || "";

    setRememberMe(shouldRemember);

    if (shouldRemember) {
      form.setFieldsValue({ phone: rememberedPhone });
    } else {
      form.setFieldsValue({ phone: "" });
    }
  }, [form]);

  return (
    <Container fluid className='login-bg'>
      <Container>
        <Row className="justify-content-center">
          <Col lg={10}>
            <div className="login-card">
              {/* LEFT PANEL (Form) */}
              <div className="login-left">
                <div className="login-form-wrap">
                  <h2 className="login-title mb-1">Welcome 👋</h2>
                  <p className="login-subtitle mb-4">Welcome back to IYE</p>

                  <Form
                    form={form}
                    className="login-form"
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    onValuesChange={() => {
                      if (error) {
                        dispatch(clearAuthError());
                      }
                    }}
                  >

                    {/* Phone */}
                    <IyeInput
                      type="tel"
                      name="phone"
                      placeholder="Phone number"
                      required
                      requiredMessage="Phone is required"
                      pattern="^\+?[0-9].*$"
                    />

                    {/* Password */}
                    <IyeInput
                      type={showConfirmPass ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      required
                      requiredMessage="Password is required"
                      prefix={<i className="fa fa-lock" />}
                      suffix={
                        showConfirmPass ? (
                          <EyeOutlined
                            onClick={() => setShowConfirmPass(false)}
                            style={{ cursor: "pointer" }}
                          />
                        ) : (
                          <EyeInvisibleOutlined
                            onClick={() => setShowConfirmPass(true)}
                            style={{ cursor: "pointer" }}
                          />
                        )
                      }
                    />

                    {error && (
                      <Alert
                        type="error"
                        message={error}
                        showIcon
                        className="mb-3"
                      />
                    )}

                    {/* Login Button */}
                    <Button
                      htmlType="submit"
                      className="login-btn w-100 rounded-pill"
                      size="large"
                      loading={loading}
                      disabled={loading}
                    >
                      {loading ? "Logging in..." : "Log In"}
                    </Button>

                    <div className="login-row-between">
                      <label className="login-remember">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(event) => setRememberMe(event.target.checked)}
                        />
                        <span>Remember me</span>
                      </label>
                    </div>

                  </Form>
                </div>
              </div>

              {/* RIGHT PANEL (Illustration) */}
              <div className="login-right">
                <div className="login-illustration-circle">
                  <img src={logo} alt="IYE" className="login-illustration" />
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </Container>
  );
}

export default Login;
